import { hasValue, isSameValue } from "../../common/helpers/Utils";

export const emptyTag = {
  TagName: "New Tag",
  SyncToHub: true,
  IsActive: true,
  LoggingFrequency: 60,
  DynamicProperties:
    '{"flags":{"High Frequency Logged":false},"isHidden":false,"manualEntry":false,"syncToHub":false}',
};

export const leftColNames = ["TagName", "TagIdentifier", "UniversalDescriptor"];

export const rightColNames = ["Delete", "Duplicate", "Edit"];

export const optionalColumns = [
  "DisplayName",
  "AssetNumber",
  "DeviceInfo",
  "MaxValue",
  "MinValue",
  "Gain",
  "Offset",
  "AlarmLoLo",
  "AlarmLo",
  "AlarmHiHi",
  "AlarmHi",
  "DisplayFormat",
  "UnitOfMeasure",
  "LoggingFrequency",
  "GroupName",
  "IsActive",
  "Description",
  "IsWriteable",
  "Transform",
  "ScanFrequency",
  "Hidden",
  "SafetyShutdown",
  "SyncToHub",
  "DynamicProperties",
  "SourceType",
  "AlarmType",
  "DataType",
  "ModbusRegister",
  "ModbusIP",
  "ModbusPort",
  "ModbusSlaveId",
  "DisplayOrder",
  "SourceTagList",
  "ModbusMapID",
  "AlarmDeadband",
  "AlarmTimeLimit",
  "AlarmSeverity",
  "AlarmLimitRange",
  "AlarmArmingType",
  "ParentTagIdentifier",
  "AlarmArmingDelay",
  "LoSetpoint",
  "LoLoSetpoint",
  "HiSetpoint",
  "HiHiSetpoint",
  "CodeLegend",
  "SyncDirection",
  "AlarmId",
  "Alarm2Id",
  "ModbusRegisterPLC",
  "ModbusRegisterRTU",
  "HwioIOType",
  "HwioChannelNumber",
  "HwioBoardNumber",
];

export const getLookupIDs = function (
  internalNameArray,
  lookupName,
  lookupValues
) {
  let lookupIdArray = [];
  for (let h = 0; h < internalNameArray.length; h++) {
    for (let i = 0; i < lookupValues.length; i++) {
      let lk = lookupValues[i];
      if (lk.LookupName === lookupName) {
        for (let j = 0; j < lk.Values.length; j++) {
          if (lk.Values[j].InternalName === internalNameArray[h]) {
            lookupIdArray.push(lk.Values[j].LookupValueId);
          }
        }
      }
    }
  }
  return lookupIdArray;
};

export const getLookupValues = function (
  valueName,
  LookupValueId,
  lookupValues
) {
  let lookupIdArray = [];
  let id = parseInt(LookupValueId);
  for (let i = 0; i < lookupValues.length; i++) {
    if (lookupValues[i].LookupName === valueName) {
      for (let j = 0; j < lookupValues[i].Values.length; j++) {
        if (lookupValues[i].Values[j].LookupValueId === id) {
          return lookupValues[i].Values[j].InternalName;
        }
      }
    }
  }
  return lookupIdArray;
};

export const lookupNameToId = function (value, category, lookupValues) {
  return getLookupIDs([value], category, lookupValues)[0];
};

export const lookupIdToName = function (id, category, lookupValues) {
  let name = getLookupValues(category, id, lookupValues);
  if (Array.isArray(name)) {
    return "";
  } else {
    return name;
  }
};

/**
 * Resolves a lookup value id from either the value's InternalName or its DisplayName. The other
 * lookup helpers match on InternalName only, but several UI surfaces present DisplayNames (the
 * grid's Source Type dropdown offers 'HardWiredIO', whose InternalName is 'HWIO'), which would
 * otherwise resolve to undefined.
 * @param {string} value An InternalName or DisplayName.
 * @param {string} category The LookupName to search, e.g. 'TagSourceType'.
 * @param {*} lookupValues The lookup values from LookupValueContext.
 * @returns {number|undefined} The matching LookupValueId, or undefined when there is no match.
 */
export const lookupNameOrDisplayNameToId = function (
  value,
  category,
  lookupValues
) {
  if (!Array.isArray(lookupValues)) {
    return undefined;
  }

  const internalNameMatch = lookupNameToId(value, category, lookupValues);
  if (internalNameMatch !== undefined) {
    return internalNameMatch;
  }

  for (const lookup of lookupValues) {
    if (lookup.LookupName !== category) {
      continue;
    }
    for (const lookupValue of lookup.Values) {
      if (lookupValue.DisplayName === value) {
        return lookupValue.LookupValueId;
      }
    }
  }

  return undefined;
};

/**
 * Returns every InternalName for a lookup category, in payload order. A payload can contain more
 * than one entry for the same LookupName, so duplicates are collapsed.
 * @param {string} category The LookupName to collect, e.g. 'TagType'.
 * @param {*} lookupValues The lookup values from LookupValueContext.
 * @returns {string[]} The InternalNames, or an empty array when the category is not loaded.
 */
export const getLookupInternalNames = (category, lookupValues) => {
  if (!Array.isArray(lookupValues)) {
    return [];
  }

  const internalNames = [];
  for (const lookup of lookupValues) {
    if (lookup.LookupName !== category) {
      continue;
    }
    for (const lookupValue of lookup.Values) {
      if (!internalNames.includes(lookupValue.InternalName)) {
        internalNames.push(lookupValue.InternalName);
      }
    }
  }

  return internalNames;
};

/**
 * Determines whether a tag belongs to one of the given source types.
 *
 * Matching prefers TagSourceTypeId, the integer lookup value the API actually returns. Only when
 * that is missing, or when the lookups needed to resolve it are not loaded, does it fall back to
 * the SourceType string. SourceType exists solely on grid rows and may hold either the lookup
 * InternalName ('HWIO', set by addLookupValueFieldsToTag) or the DisplayName ('HardWiredIO', set
 * by the grid's Source Type dropdown), so callers should pass both spellings.
 * @param {*} row The tag or grid row to test.
 * @param {string[]} sourceTypeNames InternalNames and/or DisplayNames to match against.
 * @param {*} lookupValues The lookup values from LookupValueContext.
 * @returns {boolean} True when the tag is one of the given source types.
 */
export const matchesSourceType = (row, sourceTypeNames, lookupValues) => {
  if (!row || !Array.isArray(sourceTypeNames) || sourceTypeNames.length === 0) {
    return false;
  }

  if (hasValue(row.TagSourceTypeId)) {
    const matchingIds = sourceTypeNames
      .map((name) =>
        lookupNameOrDisplayNameToId(name, "TagSourceType", lookupValues)
      )
      .filter((id) => id !== undefined);

    if (matchingIds.length > 0) {
      return matchingIds.some((id) => isSameValue(id, row.TagSourceTypeId));
    }
  }

  return sourceTypeNames.includes(row.SourceType);
};

/** Both spellings of the HardWiredIO source type: the lookup InternalName and its DisplayName. */
export const HWIO_SOURCE_TYPE_NAMES = ["HWIO", "HardWiredIO"];

/**
 * Determines whether a tag is a HardWiredIO tag. See matchesSourceType for how the match is made.
 * @param {*} row The tag or grid row to test.
 * @param {*} lookupValues The lookup values from LookupValueContext.
 * @returns {boolean} True when the tag is a HardWiredIO tag.
 */
export const isHwioTag = (row, lookupValues) =>
  matchesSourceType(row, HWIO_SOURCE_TYPE_NAMES, lookupValues);

/** HardWiredIO boards are always treated as having 64 channels, matching the shared tag editor. */
export const HWIO_CHANNEL_COUNT = 64;

/**
 * Returns the channel-number prefix for a HardWiredIO tag: the first two characters of its TagType
 * InternalName, exactly as the shared tag editor builds its channel labels. Several I/O types
 * therefore collapse to the same prefix — AI, AIC0TO20 and AIV1TO5 all yield 'AI'.
 * @param {*} row The tag or grid row.
 * @param {*} lookupValues The lookup values from LookupValueContext.
 * @returns {string} The two-character prefix, or '' when the I/O type is unset or unresolvable.
 */
export const getHwioChannelPrefix = (row, lookupValues) => {
  if (!row || !hasValue(row.TagTypeId)) {
    return "";
  }
  const internalName =
    lookupIdToName(row.TagTypeId, "TagType", lookupValues) || "";
  return internalName.substring(0, 2);
};

/**
 * Builds the list of selectable channel labels for a HardWiredIO tag, 1-based to match the shared
 * tag editor. With a prefix: 'AI 1' … 'AI 64'. Without one: '1' … '64'.
 * @param {string} prefix The two-character I/O type prefix, or '' for none.
 * @returns {string[]} The channel labels.
 */
export const getHwioChannelOptions = (prefix) => {
  return Array.from({ length: HWIO_CHANNEL_COUNT }, (_, index) =>
    prefix ? `${prefix} ${index + 1}` : String(index + 1)
  );
};

/**
 * Parses a 1-based channel number out of a value, ignoring any I/O type prefix. This is what makes
 * pasting prefix-agnostic: '5', 'AI 5' and 'DO 5' all mean channel 5.
 * @param {*} value The raw value, e.g. from a cell edit, paste, or bulk update.
 * @returns {number} The 1-based channel number, or NaN when there are no trailing digits.
 */
export const parseHwioChannelNumber = (value) => {
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : NaN;
  }
  if (!hasValue(value)) {
    return NaN;
  }
  const match = String(value)
    .trim()
    .match(/(\d+)$/);
  return match ? Number(match[1]) : NaN;
};

export const validSourceTypes = [
  "ModbusTCP",
  "ModbusRTU232",
  "ModbusRTU485",
  "CDL",
  "J1939",
  "Internal",
  "FunctionLibrary",
  "SystemMetric",
  "HWIO",
];

export const defaultDeviceParams = {
  ModbusTCP:
    '{"connection":{"ip":"0.0.0.0","port":"0","slave_id":"0"},"regnum":"0000"}',
  ModbusRTU232:
    '{"connection":{"port_number":"0","slave_id":"0"},"regnum":"0000"}',
  ModbusRTU485:
    '{"connection":{"port_number":"0","slave_id":"0"},"regnum":"0000"}',
  CDL: '{"pid":"1","mask":"","signed":false,"mid":""}',
};

export const isJsonString = (string) => {
  try {
    const parsed = JSON.parse(string);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed);
  } catch (e) {}
  return false;
};

export const parseJsonObject = (rawValue, fallbackValue = null) => {
  try {
    return JSON.parse(rawValue || "{}");
  } catch {
    return fallbackValue;
  }
};

/**
 * Coerces a value coming from an arbitrary input source (e.g. the Bulk Update modal's
 * boolean <Select>, which supplies literal strings "true"/"false") into a real boolean.
 * Used anywhere a strict boolean comparison (===) is relied upon downstream, such as the
 * grid's boolean cell icon rendering or the SyncToHub warning dialog.
 * @param {*} value The raw value to coerce.
 * @returns {boolean} The coerced boolean value.
 */
export const coerceToBoolean = (value) => {
  return value === true || String(value).toLowerCase() === "true";
};

export const updateDeviceParamsFormat = (
  deviceParams,
  sourceType,
  channelNumber
) => {
  if (!isJsonString(deviceParams)) {
    if (sourceType.startsWith("ModbusRTU")) {
      const slash = deviceParams.indexOf("/");

      if (slash === -1) {
        // This should be present
        throw new Error("DeviceParams format not supported.");
      }

      const port = deviceParams.slice(0, slash);
      const slave_id = deviceParams.slice(slash + 1);

      const deviceParamsObj = {
        connection: {
          port_number: port,
          slave_id: slave_id,
        },
        regnum: channelNumber,
      };
      return JSON.stringify(deviceParamsObj);
    } else if (sourceType === "ModbusTCP") {
      const colon = deviceParams.indexOf(":");
      const slash = deviceParams.indexOf("/");

      if (slash === -1 || colon === -1 || colon > slash) {
        // These should be present
        throw new Error("DeviceParams format not supported.");
      }

      const ip = deviceParams.slice(0, colon);
      const port = deviceParams.slice(colon + 1, slash);
      const slave_id = deviceParams.slice(slash + 1);

      const deviceParamsObj = {
        connection: {
          ip: ip,
          port: port,
          slave_id: slave_id,
        },
        regnum: channelNumber,
      };
      return JSON.stringify(deviceParamsObj);
    } else {
      return deviceParams;
    }
  } else {
    return deviceParams;
  }
};

export const tagToTagLayerViewFormat = (gridTagObject) => {
  let assetTagDTO =
    window.Enbase.Common.Tag.Normalization.BuildAssetTagDTO(gridTagObject);
  if (assetTagDTO.TagSourceTypeId) {
    assetTagDTO.SourceTypeId = assetTagDTO.TagSourceTypeId;
    delete assetTagDTO.TagSourceTypeId;
  }
  delete assetTagDTO.AssetTagId;
  assetTagDTO.TCTagLayerId = gridTagObject.TCTagLayerId ?? 0;
  return assetTagDTO;
};

const strCompareNumberAware = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
}).compare;

const isNullish = (value) => {
  return value === null || value === undefined;
};

const orderNullish = (lhs, rhs) => {
  if (isNullish(lhs)) {
    if (isNullish(rhs)) {
      return 0;
    }
    return -1;
  } else if (isNullish(rhs)) {
    return 1;
  }
  return undefined;
};

const tryParseJSON = (str) => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

const modbusPaths = {
  ModbusRegister: (deviceParams) =>
    parseInt(tryParseJSON(deviceParams)?.regnum) || 0,
  ModbusIP: (deviceParams) => tryParseJSON(deviceParams)?.connection?.ip || "",
  ModbusPort: (deviceParams) =>
    parseInt(tryParseJSON(deviceParams)?.connection?.port) || 0,
  ModbusSlave: (deviceParams) =>
    parseInt(tryParseJSON(deviceParams)?.connection?.slave_id) || 0,
};

export const getComparator = (sortColumn) => {
  switch (sortColumn) {
    case "TagName":
    case "DisplayName":
    case "AssetNumber":
    case "UniversalDescriptor":
    case "DeviceInfo":
    case "DisplayFormat":
    case "UnitOfMeasure":
    case "GroupName":
    case "Description":
    case "Transform":
    case "DynamicProperties":
    case "SourceType":
    case "AlarmType":
    case "DataType":
    case "AlarmSeverity":
    case "AlarmLimitRange":
    case "AlarmArmingType":
      return (a, b) => {
        a = a[sortColumn];
        b = b[sortColumn];
        const nullorder = orderNullish(a, b);
        if (nullorder !== undefined) {
          return nullorder;
        }
        return strCompareNumberAware(a, b);
      };
    case "TagIdentifier":
    case "ParentTagIdentifier":
    case "MaxValue":
    case "MinValue":
    case "Gain":
    case "Offset":
    case "AlarmLoLo":
    case "AlarmLo":
    case "AlarmHiHi":
    case "AlarmHi":
    case "LoggingFrequency":
    case "IsActive":
    case "IsWriteable":
    case "ScanFrequency":
    case "DisplayOrder":
    case "AlarmArmingDelay":
    case "AlarmTimeLimit":
    case "AlarmDeadband":
      return (a, b) => {
        a = a[sortColumn];
        b = b[sortColumn];
        const nullorder = orderNullish(a, b);
        if (nullorder !== undefined) {
          return nullorder;
        }
        return a - b;
      };
    case "ModbusRegister":
    case "ModbusPort":
    case "ModbusSlave":
      return (a, b) => {
        a = modbusPaths[sortColumn](a.DeviceParams);
        b = modbusPaths[sortColumn](b.DeviceParams);
        const nullorder = orderNullish(a, b);
        if (nullorder !== undefined) {
          return nullorder;
        }
        return a - b;
      };
    case "ModbusIP":
      return (a, b) => {
        a = modbusPaths[sortColumn](a.DeviceParams);
        b = modbusPaths[sortColumn](b.DeviceParams);
        const nullorder = orderNullish(a, b);
        if (nullorder !== undefined) {
          return nullorder;
        }
        return strCompareNumberAware(a, b);
      };
    default:
      throw new Error(`Cannot sort column "${sortColumn}"`);
  }
};

export const saveChangesTooltipText =
  "Saves changes made to tags in the grid back to the original source they were loaded from. For example, if you load, edit, and save changes to tags from an asset, the changes will be saved back to the asset.";

export const selectAssetsTooltipText =
  "Select assets to deploy tags to. This will open a modal where you can search for assets. This workflow is for creating new tags on the selected assets.";

export const setpointTypeToPropertyMap = {
  LoSetpoint: "LoLimit",
  LoLoSetpoint: "LoLoLimit",
  HiSetpoint: "HiLimit",
  HiHiSetpoint: "HiHiLimit",
};

export const addSetpointToDeviceParams = (
  value,
  deviceParams,
  setpointType
) => {
  setpointType = setpointTypeToPropertyMap[setpointType];
  let newDeviceParams = { ...deviceParams };
  if (!newDeviceParams.properties_to_sync) {
    newDeviceParams.properties_to_sync = {
      sync_direction: "Controller to Hub",
      properties: {},
    };
  }
  if (!newDeviceParams.properties_to_sync.properties) {
    newDeviceParams.properties_to_sync.properties = {};
  }
  if (!newDeviceParams.properties_to_sync.properties[setpointType]) {
    newDeviceParams.properties_to_sync.properties[setpointType] = {
      regnum: value,
      data_type: "SINT16BE",
      gain: "1",
      offset: "0",
    };
  }
  newDeviceParams.properties_to_sync.properties[setpointType].regnum = value;
  return newDeviceParams;
};

export const addLookupValueFieldsToTag = (tag, lookupValues) => {
  let newTag = { ...tag };
  if (tag.TagSourceTypeId) {
    newTag.SourceType = lookupIdToName(
      tag.TagSourceTypeId,
      "TagSourceType",
      lookupValues
    );
  }
  if (tag.AlarmTypeId) {
    newTag.AlarmType = lookupIdToName(
      tag.AlarmTypeId,
      "TagAlarmType",
      lookupValues
    );
  }
  if (tag.AlarmSeverityId) {
    newTag.AlarmSeverity = lookupIdToName(
      tag.AlarmSeverityId,
      "AlarmSeverity",
      lookupValues
    );
  }
  if (tag.AlarmLimitRangeId) {
    newTag.AlarmLimitRange = lookupIdToName(
      tag.AlarmLimitRangeId,
      "AlarmLimitRange",
      lookupValues
    );
  }
  if (tag.AlarmArmingTypeId) {
    newTag.AlarmArmingType = lookupIdToName(
      tag.AlarmArmingTypeId,
      "AlarmArmingType",
      lookupValues
    );
  }
  if (tag.DataTypeId) {
    newTag.DataType = lookupIdToName(
      tag.DataTypeId,
      "TagDataType",
      lookupValues
    );
  }
  return newTag;
};

export const getMaxTagIdentifier = (tags) => {
  let maxTagIdentifier = 0;
  for (let i = 0; i < tags.length; i++) {
    let tagIdentifier = parseInt(tags[i].TagIdentifier);
    // Ignore NoData tags
    if (
      tagIdentifier > maxTagIdentifier &&
      tags[i].TagName !== "NoData" &&
      tagIdentifier !== 999999999
    ) {
      maxTagIdentifier = tagIdentifier;
    }
  }
  return maxTagIdentifier;
};

const collectUsedIds = (tags, skipTagIdentifier = null) => {
  const usedIds = new Set();
  for (const tag of tags) {
    // eslint-disable-next-line eqeqeq
    if (skipTagIdentifier && tag.TagIdentifier == skipTagIdentifier) {
      continue;
    }
    if (!isNullish(tag.TagIdentifier) && tag.TagIdentifier !== "") {
      usedIds.add(parseInt(tag.TagIdentifier));
      usedIds.add(parseInt("5" + tag.TagIdentifier.toString()));
    }
    if (!isNullish(tag.AlarmId) && tag.AlarmId !== "") {
      usedIds.add(parseInt(tag.AlarmId));
    }
    if (!isNullish(tag.Alarm2Id) && tag.Alarm2Id !== "") {
      usedIds.add(parseInt(tag.Alarm2Id));
    }
  }
  return usedIds;
};

const generateSafeAlarmId = (tagIdentifier, usedIds) => {
  let alarmId = tagIdentifier;
  while (usedIds.has(alarmId)) {
    alarmId++;
  }
  return alarmId;
};

const generateSafeAlarm2Id = (tagIdentifier, usedIds) => {
  let alarm2Id = parseInt("5" + tagIdentifier.toString());
  while (usedIds.has(alarm2Id)) {
    alarm2Id++;
  }
  return alarm2Id;
};

export const generateSafeAlarmIds = (tag, allTags) => {
  const tagIdentifier = parseInt(tag.TagIdentifier);
  if (isNaN(tagIdentifier)) {
    throw new Error("Tag must have a valid TagIdentifier");
  }
  const usedIds = collectUsedIds(allTags, tagIdentifier);
  const safeAlarmId = generateSafeAlarmId(tagIdentifier, usedIds);
  usedIds.add(safeAlarmId);
  const safeAlarm2Id = generateSafeAlarm2Id(tagIdentifier, usedIds);
  return {
    safeAlarmId,
    safeAlarm2Id,
  };
};

const safeParseInt = (value) => {
  if (isNullish(value) || value === "") return null;
  const n = parseInt(value);
  return Number.isNaN(n) ? null : n;
};

export const isCurrentTagCandidate = (tag, candidate) => {
  const currentTagHasStableRecordId =
    hasValue(tag?.AssetTagId) || hasValue(tag?.TCTagLayerId);
  const sameTagIdentifier = isSameValue(
    candidate?.TagIdentifier,
    tag?.TagIdentifier
  );
  const sameAssetTagId = isSameValue(candidate?.AssetTagId, tag?.AssetTagId);
  const sameTagLayerId = isSameValue(
    candidate?.TCTagLayerId,
    tag?.TCTagLayerId
  );

  return (
    sameTagIdentifier &&
    (!currentTagHasStableRecordId || sameAssetTagId || sameTagLayerId)
  );
};

export const getParentTagCandidates = (row, allTags = []) => {
  if (!row || !Array.isArray(allTags)) {
    return [];
  }

  const seenTagIdentifiers = new Set();

  return allTags.reduce((parentTags, candidate) => {
    if (!hasValue(candidate?.TagIdentifier)) {
      return parentTags;
    }

    if (row.AssetId && !isSameValue(candidate.AssetId, row.AssetId)) {
      return parentTags;
    }

    if (isCurrentTagCandidate(row, candidate)) {
      return parentTags;
    }

    const tagIdentifierKey = candidate.TagIdentifier.toString();
    if (seenTagIdentifiers.has(tagIdentifierKey)) {
      return parentTags;
    }

    seenTagIdentifiers.add(tagIdentifierKey);
    parentTags.push(candidate);
    return parentTags;
  }, []);
};

export const findParentTagCandidate = (row, allTags) => {
  return getParentTagCandidates(row, allTags).find((candidate) =>
    isSameValue(candidate.TagIdentifier, row?.ParentTagIdentifier)
  );
};

/**
 * The Tag Identifier of a tag's parent. Uses ParentTagIdentifier when set; the API can return only
 * ParentAssetTagId (tag-list layers store the two independently), which resolves through the grid
 * tag with that AssetTagId.
 * @param {object} tag
 * @param {object[]} allTags The grid's tags.
 * @returns {number|string|null} null when there is no parent or it isn't in the grid.
 */
export const resolveParentTagIdentifier = (tag, allTags = []) => {
  if (hasValue(tag?.ParentTagIdentifier)) {
    return tag.ParentTagIdentifier;
  }
  if (!hasValue(tag?.ParentAssetTagId)) {
    return null;
  }
  const parent = allTags.find((candidate) =>
    isSameValue(candidate.AssetTagId, tag.ParentAssetTagId)
  );
  return hasValue(parent?.TagIdentifier) ? parent.TagIdentifier : null;
};

/**
 * The parent fields to store after a tag dialog save. The dialog's parent field holds a Tag
 * Identifier. An unchanged parent keeps the source tag's own parent fields, so a parent the dialog
 * could not show survives; a changed one replaces them.
 * @param {object} sourceTag The tag the dialog was opened for.
 * @param {number|string|null} shownParent What the dialog's parent field showed when it opened.
 * @param {*} savedParent The parent field's value on save.
 * @returns {{ ParentTagIdentifier?: number|string|null, ParentAssetTagId?: number }}
 */
export const parentFieldsAfterDialogSave = (
  sourceTag,
  shownParent,
  savedParent
) => {
  const parsed = parseInt(savedParent);
  const chosenParent = Number.isNaN(parsed) ? null : parsed;
  const unchanged =
    (!hasValue(chosenParent) && !hasValue(shownParent)) ||
    isSameValue(chosenParent, shownParent);

  if (!unchanged) {
    return { ParentTagIdentifier: chosenParent };
  }
  return {
    ...(hasValue(shownParent) && { ParentTagIdentifier: shownParent }),
    ...(hasValue(sourceTag.ParentAssetTagId) && {
      ParentAssetTagId: sourceTag.ParentAssetTagId,
    }),
  };
};

export const validateTagIdUniquenessSingleAsset = (tags) => {
  const conflicts = [];
  const tagIdentifierMap = new Map();
  const alarmIdsMap = new Map();

  for (const tag of tags) {
    const tagIdentifier = safeParseInt(tag.TagIdentifier);
    const alarmId = safeParseInt(tag.AlarmId);
    const alarm2Id = safeParseInt(tag.Alarm2Id);

    if (tagIdentifier !== null) {
      const existingTag = tagIdentifierMap.get(tagIdentifier);
      if (existingTag && existingTag !== tag) {
        conflicts.push(
          `TagID ${tagIdentifier} is used by tags "${existingTag.TagName}" and "${tag.TagName}"`
        );
      } else {
        tagIdentifierMap.set(tagIdentifier, tag);
      }
    }

    const processAlarm = (value, fieldName) => {
      if (value === null) return;
      const existing = alarmIdsMap.get(value);
      if (existing && existing.tag !== tag) {
        conflicts.push(
          `${fieldName} ${value} conflicts with ${existing.field} of tag "${existing.tag.TagName}" and tag "${tag.TagName}"`
        );
      } else if (
        existing &&
        existing.tag === tag &&
        existing.field !== fieldName
      ) {
        // Same tag using same value for both AlarmId and Alarm2Id
        conflicts.push(
          `Tag "${tag.TagName}" uses identical value ${value} for both AlarmId and Alarm2Id`
        );
      } else {
        alarmIdsMap.set(value, { tag, field: fieldName });
      }
    };

    processAlarm(alarmId, "AlarmId");
    processAlarm(alarm2Id, "Alarm2Id");
  }

  if (conflicts.length) {
    throw new Error(conflicts.join("\n"));
  }
};

export const validateTagIdUniqueness = (tags) => {
  const assetGroups = new Map();

  for (const tag of tags) {
    const assetId = safeParseInt(tag.AssetId);

    if (!assetGroups.has(assetId)) {
      assetGroups.set(assetId, []);
    }
    assetGroups.get(assetId).push(tag);
  }

  const allErrors = [];

  for (const [assetId, assetTags] of assetGroups) {
    try {
      validateTagIdUniquenessSingleAsset(assetTags);
    } catch (error) {
      const errorLines = error.message.split("\n");
      const contextualErrors = errorLines.map((line) => {
        return `[AssetId ${assetId}] ${line}`;
      });
      allErrors.push(...contextualErrors);
    }
  }

  if (allErrors.length) {
    throw new Error(allErrors.join("\n"));
  }
};

export const getTagsWithUnresolvedParent = (tagsToCheck, allTags) => {
  if (!Array.isArray(tagsToCheck) || !Array.isArray(allTags)) {
    return [];
  }

  return tagsToCheck.filter((tag) => {
    if (!tag?.AssetId || !hasValue(tag.ParentTagIdentifier)) {
      return false;
    }

    return !findParentTagCandidate(tag, allTags);
  });
};

export const getTagsWithExcessiveParentTiers = (tags) => {
  if (!Array.isArray(tags)) {
    return [];
  }

  const tagsByIdentifier = new Map();
  for (const tag of tags) {
    if (hasValue(tag?.TagIdentifier)) {
      tagsByIdentifier.set(tag.TagIdentifier.toString(), tag);
    }
  }

  const findParentInBatch = (tag) => {
    if (!hasValue(tag?.ParentTagIdentifier)) {
      return undefined;
    }
    const parent = tagsByIdentifier.get(tag.ParentTagIdentifier.toString());
    return parent && parent !== tag ? parent : undefined;
  };

  const excessiveChains = [];
  for (const tag of tags) {
    const parent = findParentInBatch(tag);
    if (!parent) {
      continue;
    }
    const grandparent = findParentInBatch(parent);
    if (grandparent) {
      excessiveChains.push({ tag, parent, grandparent });
    }
  }

  return excessiveChains;
};

export const readArrayFromStorage = (storageKey, fallbackValue = []) => {
  try {
    const storedValue = localStorage.getItem(storageKey);
    if (!storedValue) {
      return fallbackValue;
    }

    const parsedValue = JSON.parse(storedValue);
    return Array.isArray(parsedValue) ? parsedValue : fallbackValue;
  } catch {
    return fallbackValue;
  }
};

export const lookupIdToNameMappings = {
  Alarm2ArmingTypeId: "AlarmArmingType",
  Alarm2LimitRangeId: "AlarmLimitRange",
  Alarm2TypeId: "AlarmType",
  AlarmArmingTypeId: "AlarmArmingType",
  AlarmLimitRangeId: "AlarmLimitRange",
  AlarmSeverityId: "AlarmSeverity",
  AlarmTypeId: "AlarmType",
  DataTypeId: "TagDataType",
  TagSourceTypeId: "TagSourceType",
  TagTypeId: "TagType",
};
